import React, { useState } from 'react';
import type { Contract } from 'src/types/contract.type';
import type { ContractMilestone } from 'src/types/contractMilestone.type';
import type { ActualPayment } from 'src/types/actualPayment.type';
import { toProjectDateOnly } from 'src/utils/date';
import { useProjectSettingCtx } from 'src/features/projects/contexts/ProjectSettingContext';

interface Props {
    contract?: Contract;
    milestones: ContractMilestone[];
    canEdit?: boolean;
    onAddPayment?: (milestoneId: string, amountPlan: number, currentPaid: number) => void;
}

export default function IntegratedPaymentMilestoneTable({
    contract,
    milestones,
    canEdit,
    onAddPayment,
}: Props) {
    const { setting } = useProjectSettingCtx();
    const zone = setting?.timeZone ?? 'Asia/Seoul';

    const [expandedMilestones, setExpandedMilestones] = useState<Set<string>>(new Set());

    const toggleExpand = (id: string) => {
        setExpandedMilestones((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const formatCurrency = (val: number | null | undefined) => {
        if (val == null) return '—';
        return new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 0 }).format(val);
    };

    // WBS-style Status Badge logic (replicated for consistency)
    const getStatusStyle = (status: string) => {
        switch (status) {
            case 'PAID':
            case 'COMPLETED': return 'bg-emerald-100 text-emerald-700';
            case 'PARTIAL_PAID': return 'bg-blue-100 text-blue-700';
            case 'IN_PROGRESS': return 'bg-sky-100 text-sky-700';
            case 'DELAYED': return 'bg-rose-100 text-rose-700';
            default: return 'bg-gray-100 text-gray-700';
        }
    };

    return (
        <div className="bg-white rounded-2xl border overflow-hidden">
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                    <thead>
                        <tr className="bg-gray-50 text-left text-gray-500 uppercase text-[10px] tracking-wider border-b">
                            <th className="px-4 py-3 w-10"></th>
                            <th className="px-4 py-3 w-16">No.</th>
                            <th className="px-4 py-3">Milestone Activity</th>
                            <th className="px-4 py-3 text-right">%</th>
                            <th className="px-4 py-3 text-right">Plan Amount</th>
                            <th className="px-4 py-3 text-right">Actual Paid</th>
                            <th className="px-4 py-3">Due Date</th>
                            <th className="px-4 py-3">Actual Date</th>
                            <th className="px-4 py-3">Status</th>
                            {canEdit && <th className="px-4 py-3 w-20">Actions</th>}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {milestones.map((m, idx) => {
                            const isExpanded = expandedMilestones.has(m.id);
                            const payments = (m as any).payments as ActualPayment[] ?? [];
                            const totalPaid = payments.reduce((acc, p) => acc + (p.amount ?? 0), 0);
                            const isFullyPaid = m.status === 'PAID' || (m.amountPlan != null && totalPaid >= m.amountPlan);
                            const isPartialPaid = !isFullyPaid && totalPaid > 0;
                            
                            // 마지막 지급일 또는 milestone의 완료일을 Actual Date로 표시
                            const lastPaidDate = payments.length > 0 
                                ? payments[payments.length - 1].paidAt 
                                : (m as any).eventDate;

                            let displayStatus = (m as any).status ?? 'PLANNED';
                            if (isFullyPaid) displayStatus = 'PAID';
                            else if (isPartialPaid) displayStatus = 'PARTIAL_PAID';

                            return (
                                <React.Fragment key={m.id}>
                                    {/* Milestone Row */}
                                    <tr className={`hover:bg-gray-50 transition-colors ${isExpanded ? 'bg-gray-50/50' : ''}`}>
                                        <td className="px-4 py-4">
                                            <button 
                                                onClick={() => toggleExpand(m.id)}
                                                className="p-1 hover:bg-gray-200 rounded transition-colors"
                                            >
                                                <svg 
                                                    className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} 
                                                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                                </svg>
                                            </button>
                                        </td>
                                        <td className="px-4 py-4 text-gray-500">
                                            {m.seq || idx + 1}
                                        </td>
                                        <td className="px-4 py-4">
                                            <div className="font-semibold text-gray-900">{m.label}</div>
                                            {m.note && <div className="text-[11px] text-gray-500 mt-0.5">{m.note}</div>}
                                        </td>
                                        <td className="px-4 py-4 text-right text-gray-500">
                                            {(m as any).percent != null ? `${(m as any).percent}%` : '—'}
                                        </td>
                                        <td className="px-4 py-4 text-right font-medium text-gray-700">
                                            {formatCurrency(m.amountPlan)}
                                        </td>
                                        <td className="px-4 py-4 text-right">
                                            <span className={totalPaid > 0 ? (isFullyPaid ? 'font-bold text-emerald-600' : 'font-bold text-blue-600') : 'text-gray-400'}>
                                                {formatCurrency(totalPaid)}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 text-gray-500 text-xs">
                                            {toProjectDateOnly(m.dueDate, zone) || '—'}
                                        </td>
                                        <td className="px-4 py-4 text-gray-500 text-xs">
                                            {toProjectDateOnly(lastPaidDate, zone) || '—'}
                                        </td>
                                        <td className="px-4 py-4">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${getStatusStyle(displayStatus)}`}>
                                                {displayStatus === 'PARTIAL_PAID' ? 'PARTIAL' : displayStatus}
                                            </span>
                                        </td>
                                        {canEdit && (
                                            <td className="px-4 py-4 text-right">
                                                <button 
                                                    onClick={() => onAddPayment?.(m.id, m.amountPlan ?? 0, totalPaid)}
                                                    disabled={isFullyPaid}
                                                    className={`text-xs font-semibold px-2 py-1 rounded transition-colors inline-flex items-center gap-1 ${
                                                        isFullyPaid 
                                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                                                            : 'bg-slate-900 text-white hover:bg-slate-700'
                                                    }`}
                                                    title={isFullyPaid ? '지급이 완료되었습니다.' : '지급 내역 추가'}
                                                >
                                                    <span>+</span>
                                                    <span>지급</span>
                                                </button>
                                            </td>
                                        )}
                                    </tr>

                                    {/* Payments Rows (Nested) */}
                                    {isExpanded && (
                                        <>
                                            {payments.map((p) => (
                                                <tr key={p.id} className="bg-emerald-50/20 border-l-4 border-emerald-400">
                                                    <td className="px-4 py-3"></td>
                                                    <td className="px-4 py-3" colSpan={2}>
                                                        <div className="flex items-center gap-2">
                                                            <svg className="w-3 h-3 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                            </svg>
                                                            <span className="text-xs text-gray-600 font-medium">Payment Executed</span>
                                                            {p.note && <span className="text-[10px] text-gray-400 italic">({p.note})</span>}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-right"></td>
                                                    <td className="px-4 py-3 text-right"></td>
                                                    <td className="px-4 py-3 text-right text-emerald-600 font-bold">
                                                        {formatCurrency(p.amount)}
                                                    </td>
                                                    <td className="px-4 py-3"></td>
                                                    <td className="px-4 py-3 text-xs text-gray-500 italic">
                                                        {toProjectDateOnly(p.paidAt, zone) || '—'}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <span className="text-[10px] uppercase font-bold text-emerald-600">Paid</span>
                                                    </td>
                                                    {canEdit && <td className="px-4 py-3"></td>}
                                                </tr>
                                            ))}
                                            {payments.length === 0 && (
                                                <tr className="bg-gray-50/30">
                                                    <td className="px-4 py-3"></td>
                                                    <td colSpan={canEdit ? 9 : 8} className="px-4 py-3 text-xs text-gray-400 italic">
                                                        No payment records found for this milestone.
                                                    </td>
                                                </tr>
                                            )}
                                        </>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
